import { useState, useEffect } from 'react';
import {
  Box, Typography, Button, TextField, Tabs, Tab, Paper, Grid,
  Autocomplete, CircularProgress, ToggleButtonGroup, ToggleButton,
  InputAdornment, FormControlLabel, Switch, Checkbox, Divider, Alert,
  MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, Chip,
  IconButton,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
  Description as DescriptionIcon,
  LocationOn as LocationIcon,
  AttachMoney as SalaryIcon,
  HelpOutline as QuestionsIcon,
  AccountTree as PipelineIcon,
  BookmarkAdd as SaveTemplateIcon,
  InfoOutlined as FieldInfoIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import useStore from '../../config/store';
import AtsPipelineConfigurator from './AtsPipelineConfigurator';
import AtsFilteringQuestions from './AtsFilteringQuestions';
import AtsCaptationFlowEditor from './AtsCaptationFlowEditor';
import { canEditPosition } from './positionPermissions';
import {
  buildDefaultPipeline, buildMassivePipeline, pipelineFromTemplate, hasBackgroundCheckStage, withoutBackgroundCheck,
} from './defaultPipeline';
import useOrganizationCountry from '../../hooks/useOrganizationCountry';
import { STATUSES as POSITION_STATUSES, isVisibleInPortal } from './positionStatus';

const CURRENCIES = ['CLP', 'USD', 'EUR', 'ARS', 'COP', 'PEN', 'UYU', 'MXN', 'BRL'];
// Renta: rango (min/max) o sueldo único (monto). Mutuamente excluyentes.
const TIPOS_SALARIO = ['rango', 'unico'];
const MODALIDADES = ['presencial', 'remoto', 'hibrido'];
const PRIORIDADES = ['alta', 'media', 'baja'];
const TIPOS_BUSQUEDA = ['internal', 'external', 'general'];
const STATUSES = POSITION_STATUSES;
const VISIBILITIES = ['general', 'confidential'];
// 'vacancy' quedó deprecado (vacantes existentes se normalizan a 'hunting' al editar)
const TIPOS_PROCESO = ['hunting', 'massive'];

// Competencias predeterminadas del cargo: mismo campo `competencias` que
// Gestión de Cargos, guardado como [{ competencyId, competencyName }]. Acepta
// opciones del catálogo (GET /capabilities → { id, name }) o ya guardadas.
const normalizeCompetencia = (c) => ({
  competencyId: c.competencyId ?? c.id ?? c.uid,
  competencyName: c.competencyName ?? c.name ?? '',
});

// Lista guardada en la vacante → valor del formulario / payload. Descarta
// entradas sin id (Firestore no acepta undefined al guardar).
const normalizeCompetencias = (list) => (Array.isArray(list) ? list : [])
  .filter((c) => c && typeof c === 'object')
  .map(normalizeCompetencia)
  .filter((c) => c.competencyId);

const INITIAL_FORM = {
  name: '',
  internalName: '',
  // Vacantes nuevas: bot (masivas) y "Visible en portal" (portalEnabled)
  // encendidos (PLAN_MASIVO_PORTAL)
  captacion: { enabled: true, portalEnabled: true },
  industria: '',
  jerarquia: '',
  codigoRef: '',
  tipo: 'hunting',
  area: '',
  subArea: '',
  numPosiciones: '',
  prioridad: 'media',
  status: 'draft',
  visibility: 'general',
  autoSendRejectionEmail: false,
  motivoBusqueda: '',
  descripcion: '',
  responsabilidades: '',
  perfil: '',
  conocimientosExcluyentes: '',
  conocimientosDeseables: '',
  // Competencias predeterminadas: [{ competencyId, competencyName }]
  competencias: [],
  additionalInfoUrl: '',
  // Beneficios del cargo: [{ nombre, descripcion }] (nombre obligatorio)
  beneficios: [],
  modalidad: 'presencial',
  ubicacion: { country: '', province: '', city: '' },
  fechaApertura: '',
  fechaCierre: '',
  tipoBusqueda: null,
  salarioRango: {
    tipo: 'rango',
    min: '',
    max: '',
    monto: '',
    currency: 'CLP',
    visible: true,
  },
  // Bonos del cargo: [{ nombre, descripcion, monto }] (nombre obligatorio).
  // Solo contexto para el bot; respetan el switch "mostrar salario".
  bonos: [],
  // Condiciones económicas del encargo (consultora ↔ cliente). Dato interno:
  // nunca se muestra al candidato.
  condicionesPago: {
    currency: 'CLP',
    precio: '',
    porcentajeKickoff: '',
    porcentajeCierre: '',
  },
  filteringQuestions: [],
  // Pipeline estándar del sistema (ver defaultPipeline.js). Al crear una
  // vacante puede reemplazarse por una plantilla del proyecto.
  pipeline: buildDefaultPipeline(),
  source: 'ats',
};

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// Lista editable de ítems { nombre, descripcion, monto? } (bonos y beneficios).
// Sin estado propio: el padre es dueño de `items`.
const ItemListEditor = ({
  items, onChange, labels, showMonto = false, currency = '', errorIndexes = [], emptyText,
}) => {
  const update = (index, key, value) => {
    onChange(items.map((it, i) => (i === index ? { ...it, [key]: value } : it)));
  };
  const remove = (index) => onChange(items.filter((_, i) => i !== index));
  const add = () => onChange([
    ...items,
    showMonto ? { nombre: '', descripcion: '', monto: '' } : { nombre: '', descripcion: '' },
  ]);

  return (
    <Box>
      {items.length === 0 && emptyText && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{emptyText}</Typography>
      )}
      {items.map((it, index) => (
        <Grid container spacing={1} alignItems="flex-start" key={index} sx={{ mb: 1 }}>
          <Grid item xs={12} md={showMonto ? 3 : 4}>
            <TextField
              label={labels.nombre}
              value={it.nombre || ''}
              onChange={(e) => update(index, 'nombre', e.target.value)}
              fullWidth
              size="small"
              required
              error={errorIndexes.includes(index)}
              helperText={errorIndexes.includes(index) ? labels.required : ''}
            />
          </Grid>
          <Grid item xs={12} md={showMonto ? 5 : 7}>
            <TextField
              label={labels.descripcion}
              value={it.descripcion || ''}
              onChange={(e) => update(index, 'descripcion', e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
          {showMonto && (
            <Grid item xs={10} md={3}>
              <TextField
                label={labels.monto}
                value={it.monto ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || parseFloat(val) >= 0) update(index, 'monto', val);
                }}
                fullWidth
                size="small"
                type="number"
                inputProps={{ min: 0 }}
                InputProps={{
                  startAdornment: currency && (
                    <InputAdornment position="start">{currency}</InputAdornment>
                  ),
                }}
              />
            </Grid>
          )}
          <Grid item xs={2} md={1} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip title={labels.remove}>
              <IconButton size="small" color="error" onClick={() => remove(index)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={add}>
        {labels.add}
      </Button>
    </Box>
  );
};

// Label de campo con ícono informativo (tooltip explicando qué significa)
const LabelWithInfo = ({ label, tooltip }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Tooltip title={tooltip} placement="right">
      <FieldInfoIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
    </Tooltip>
  </Box>
);

const AtsPositionWizard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { positionId } = useParams();
  const location = useLocation();
  // Modo duplicar: carga la vacante origen pero guarda como creación (POST).
  // El tipo de proceso queda editable porque la copia aún no existe.
  const isDuplicate = !!positionId && location.pathname.endsWith('/duplicate');
  const isEdit = !!positionId && !isDuplicate;
  // Fuera de Chile no hay verificación de antecedentes (en espera, PLAN_MASIVO_PORTAL)
  const { operatesInChile, loading: orgCountryLoading } = useOrganizationCountry();
  const massivePipeline = () => (operatesInChile ? buildMassivePipeline() : withoutBackgroundCheck(buildMassivePipeline()));

  const industriasOptions = t('industries', { returnObjects: true });

  const { organization, project, authToken, user, showAlert, organizationName, language } = useStore.getState();

  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState(() => ({
    ...INITIAL_FORM,
    pipeline: buildDefaultPipeline(),
  }));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit || isDuplicate);
  const [loadError, setLoadError] = useState(null);
  // Opt-in de las condiciones de pago. No se persiste como campo aparte: al
  // editar se marca solo si la vacante ya tiene condiciones guardadas.
  const [condicionesPagoEnabled, setCondicionesPagoEnabled] = useState(false);

  // Plantillas de pipeline del proyecto (solo modo creación)
  const [pipelineTemplates, setPipelineTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('standard');
  const [pipelineTouched, setPipelineTouched] = useState(false);
  // Plantilla elegida con el pipeline ya modificado: espera confirmación en un modal
  const [pendingTemplateId, setPendingTemplateId] = useState(null);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  // Plantillas de CAPTACIÓN (F6) — mismo patrón que las de pipeline
  const [captationTemplates, setCaptationTemplates] = useState([]);
  const [selectedCaptationTemplateId, setSelectedCaptationTemplateId] = useState('standard');
  const [saveCaptationTplOpen, setSaveCaptationTplOpen] = useState(false);
  const [captationTplName, setCaptationTplName] = useState('');
  const [savingCaptationTpl, setSavingCaptationTpl] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const isAdminPlus = ['admin', 'admin-grafton', 'super-admin'].includes(user?.rol);

  // Catálogo del campo "Competencias predeterminadas" (mismo endpoint y
  // parámetros que Gestión de Cargos). Crear, editar y duplicar.
  const [competenciesOptions, setCompetenciesOptions] = useState([]);
  const [loadingCompetencies, setLoadingCompetencies] = useState(false);

  useEffect(() => {
    if (!authToken || !organization) return;

    const loadCompetencies = async () => {
      try {
        setLoadingCompetencies(true);
        const response = await api.get('/capabilities', {
          params: { language, organizationId: organization },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (response.data.errorCode === 0 && Array.isArray(response.data.response?.data)) {
          setCompetenciesOptions(response.data.response.data);
        }
      } catch (err) {
        // Silencioso: sin catálogo no se pueden agregar, pero las guardadas se ven igual
        console.error('Error loading competencies:', err);
      } finally {
        setLoadingCompetencies(false);
      }
    };

    loadCompetencies();
    // Solo al montar, como las plantillas
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar plantillas de pipeline del proyecto (solo al crear). Si hay una
  // predeterminada, se precarga en lugar del pipeline estándar del sistema.
  useEffect(() => {
    if (isEdit || !organization || !project) return;

    const loadTemplates = async () => {
      try {
        const res = await api.get(`/projects/ats-pipeline-templates`, {
          params: { organizationId: organization, projectId: project },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const list = res.data.response?.data;
        const templates = Array.isArray(list) ? list : [];
        setPipelineTemplates(templates);

        const defaultTemplate = templates.find((tpl) => tpl.isDefault);
        if (defaultTemplate?.pipeline?.phases?.length) {
          setSelectedTemplateId(defaultTemplate.id);
          setFormData((prev) => ({
            ...prev,
            pipeline: pipelineFromTemplate(defaultTemplate),
          }));
        }
      } catch (err) {
        // Silencioso: sin plantillas se usa el pipeline estándar
        console.error(err);
      }

      // Plantillas de captación (F6): la predeterminada se aplica sola —
      // la vacante nueva "nace configurada" con el flujo de la empresa
      try {
        const res = await api.get(`/projects/captation-templates`, {
          params: { organizationId: organization, projectId: project },
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const list = res.data.response?.data;
        const templates = Array.isArray(list) ? list : [];
        setCaptationTemplates(templates);

        const defaultTemplate = templates.find((tpl) => tpl.isDefault);
        if (defaultTemplate?.captacion) {
          setSelectedCaptationTemplateId(defaultTemplate.id);
          setFormData((prev) => ({
            ...prev,
            captacion: {
              ...(prev.captacion || { enabled: true }),
              flow: defaultTemplate.captacion.flow || null,
              rules: defaultTemplate.captacion.rules || null,
            },
          }));
        }
      } catch (err) {
        // Silencioso: sin plantillas se usa el flujo estándar del sistema
        console.error(err);
      }
    };

    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar datos si es modo edición o duplicación
  useEffect(() => {
    if (!isEdit && !isDuplicate) return;

    const loadPosition = async () => {
      try {
        setLoading(true);
        const response = await api.get(
          `/positions/${positionId}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );

        if (response.data.response?.status_response === 'OK') {
          const p = response.data.response.data;

          // Gating de edición (solo ATS): si el usuario no puede editar, no se
          // permite entrar a la pantalla de edición. El backend igual lo bloquea.
          if (!canEditPosition(p, user)) {
            showAlert(t('ats.positions.noEditPermission'), 'warning');
            navigate(`/ats/positions/${positionId}`);
            return;
          }

          setFormData({
            name: p.name || '',
            industria: p.industria || '',
            jerarquia: p.jerarquia || '',
            codigoRef: p.codigoRef || '',
            internalName: p.internalName || '',
            // "Visible en portal": si nunca se guardó, sale del antiguo "Tipo
            // de publicación" (hunting) o queda apagado (masivas antiguas)
            captacion: { ...(p.captacion || { enabled: true }), portalEnabled: isVisibleInPortal(p) },
            tipo: (p.tipo === 'vacancy' ? 'hunting' : p.tipo) || 'hunting',
            area: p.area || '',
            subArea: p.subArea || '',
            numPosiciones: p.numPosiciones || '',
            prioridad: p.prioridad || 'media',
            status: p.status || 'draft',
            visibility: p.visibility || 'general',
            autoSendRejectionEmail: p.autoSendRejectionEmail || false,
            motivoBusqueda: p.motivoBusqueda || '',
            descripcion: p.descripcion || '',
            responsabilidades: p.responsabilidades || '',
            perfil: p.perfil || '',
            conocimientosExcluyentes: p.conocimientosExcluyentes || '',
            conocimientosDeseables: p.conocimientosDeseables || '',
            competencias: normalizeCompetencias(p.competencias),
            additionalInfoUrl: p.additionalInfoUrl || '',
            beneficios: Array.isArray(p.beneficios)
              ? p.beneficios.filter((b) => b && typeof b === 'object').map((b) => ({
                nombre: b.nombre || '',
                descripcion: b.descripcion || '',
              }))
              : [],
            modalidad: p.modalidad || 'presencial',
            ubicacion: p.ubicacion || { country: '', province: '', city: '' },
            fechaApertura: p.fechaApertura || '',
            fechaCierre: p.fechaCierre || '',
            tipoBusqueda: p.tipoBusqueda || null,
            salarioRango: {
              tipo: p.salarioRango?.tipo === 'unico' ? 'unico' : 'rango',
              min: p.salarioRango?.min ?? '',
              max: p.salarioRango?.max ?? '',
              monto: p.salarioRango?.monto ?? '',
              currency: p.salarioRango?.currency || 'CLP',
              visible: p.salarioRango?.visible ?? true,
            },
            bonos: Array.isArray(p.bonos)
              ? p.bonos.filter((b) => b && typeof b === 'object').map((b) => ({
                nombre: b.nombre || '',
                descripcion: b.descripcion || '',
                monto: b.monto ?? '',
              }))
              : [],
            condicionesPago: {
              currency: p.condicionesPago?.currency || 'CLP',
              precio: p.condicionesPago?.precio ?? '',
              porcentajeKickoff: p.condicionesPago?.porcentajeKickoff ?? '',
              porcentajeCierre: p.condicionesPago?.porcentajeCierre ?? '',
            },
            filteringQuestions: p.filteringQuestions || [],
            pipeline: p.pipeline || buildDefaultPipeline(),
            source: p.source || 'ats',
            // Overrides al duplicar: la copia nace en borrador, con nombre
            // marcado y sin código de referencia ni fechas de la original.
            ...(isDuplicate ? {
              internalName: `${p.internalName || p.name || ''} (copia)`.trim(),
              status: 'draft',
              codigoRef: '',
              fechaApertura: '',
              fechaCierre: '',
              pipeline: JSON.parse(JSON.stringify(p.pipeline || buildDefaultPipeline())),
              filteringQuestions: JSON.parse(JSON.stringify(p.filteringQuestions || [])),
            } : {}),
          });
          // El checkbox nace marcado si la vacante ya trae condiciones cargadas
          setCondicionesPagoEnabled(!!p.condicionesPago?.precio);
        } else {
          setLoadError(t('ats.positions.loadError'));
        }
      } catch (err) {
        console.error(err);
        setLoadError(t('ats.positions.loadError'));
      } finally {
        setLoading(false);
      }
    };

    loadPosition();
    // authToken fuera de deps: su refresh (cada 5 min) recargaba la vacante y
    // borraba lo escrito en el formulario. Mismo patrón que AtsPositionDetailLayout.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionId]);

  const handleField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const handleNestedField = (parent, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value },
    }));
  };

  // Condiciones económicas: monto que representa cada tramo sobre el precio del
  // cargo, y suma de los porcentajes para avisar si no cierra en 100%.
  const montoTramo = (porcentaje) => {
    const precio = parseFloat(formData.condicionesPago.precio);
    const pct = parseFloat(porcentaje);
    if (!precio || Number.isNaN(pct)) return ' ';
    const monto = (precio * pct) / 100;
    return t('ats.positions.wizard.condicionesPago.montoTramo', {
      monto: `${formData.condicionesPago.currency || ''} ${monto.toLocaleString()}`.trim(),
    });
  };

  // Los dos tramos son complementarios: al escribir uno, el otro se completa
  // solo para que siempre sumen 100%.
  const handlePorcentajeTramo = (field, value) => {
    const otro = field === 'porcentajeKickoff' ? 'porcentajeCierre' : 'porcentajeKickoff';
    setFormData((prev) => ({
      ...prev,
      condicionesPago: {
        ...prev.condicionesPago,
        [field]: value,
        [otro]: value === '' ? '' : String(100 - parseFloat(value)),
      },
    }));
  };

  const handleSalarioRango = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      salarioRango: { ...prev.salarioRango, [field]: value },
    }));
  };

  // Organización fuera de Chile: una vacante NUEVA no trae la etapa
  // "Verificación de antecedentes", aunque venga de una plantilla o de un
  // duplicado. Las ya creadas no se tocan (podrían tener candidatos ahí).
  useEffect(() => {
    if (isEdit || orgCountryLoading || operatesInChile) return;
    setFormData((prev) => (hasBackgroundCheckStage(prev.pipeline)
      ? { ...prev, pipeline: withoutBackgroundCheck(prev.pipeline) }
      : prev));
  }, [isEdit, orgCountryLoading, operatesInChile, formData.pipeline]);

  // ── Plantillas de pipeline (solo creación) ────────────────────────────────

  const applyPipelineTemplate = (value) => {
    if (value === 'standard') {
      setFormData((prev) => ({
        ...prev,
        pipeline: prev.tipo === 'massive' ? massivePipeline() : buildDefaultPipeline(),
      }));
    } else {
      const template = pipelineTemplates.find((tpl) => tpl.id === value);
      if (!template) return;
      setFormData((prev) => ({ ...prev, pipeline: pipelineFromTemplate(template) }));
    }
    setSelectedTemplateId(value);
    setPipelineTouched(false);
  };

  const handleSelectTemplate = (value) => {
    if (value === selectedTemplateId) return;
    // Si el usuario ya modificó el pipeline a mano, confirmar el reemplazo en
    // un modal (pendingTemplateId) antes de aplicar la plantilla
    if (pipelineTouched) {
      setPendingTemplateId(value);
      return;
    }
    applyPipelineTemplate(value);
  };

  // Aplicar una plantilla de captación al formulario ('standard' = flujo del sistema)
  const handleSelectCaptationTemplate = (value) => {
    if (value === selectedCaptationTemplateId) return;
    setSelectedCaptationTemplateId(value);
    if (value === 'standard') {
      setFormData((prev) => ({
        ...prev,
        captacion: { ...(prev.captacion || { enabled: true }), flow: null, rules: null },
      }));
      return;
    }
    const template = captationTemplates.find((tpl) => tpl.id === value);
    if (!template?.captacion) return;
    setFormData((prev) => ({
      ...prev,
      captacion: {
        ...(prev.captacion || { enabled: true }),
        flow: template.captacion.flow || null,
        rules: template.captacion.rules || null,
      },
    }));
  };

  const handleSaveCaptationAsTemplate = async () => {
    const name = captationTplName.trim();
    if (!name) return;
    if (!formData.captacion?.flow && !formData.captacion?.rules) {
      showAlert(t('ats.positions.wizard.captacionBot.saveAsTemplateDisabledHint'), 'warning');
      return;
    }
    setSavingCaptationTpl(true);
    try {
      const res = await api.post(
        `/projects/captation-templates`,
        {
          name,
          captacion: {
            flow: formData.captacion?.flow || null,
            rules: formData.captacion?.rules || null,
          },
        },
        {
          params: { organizationId: organization, projectId: project },
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      if (res.data.response?.status_response === 'OK') {
        const created = res.data.response.data;
        setCaptationTemplates((prev) => [...prev, created]);
        setSelectedCaptationTemplateId(created.id);
        setSaveCaptationTplOpen(false);
        setCaptationTplName('');
        showAlert(t('ats.positions.wizard.captacionBot.templateSaved'), 'success');
      } else {
        showAlert(t('ats.positions.wizard.captacionBot.templateSaveError'), 'error');
      }
    } catch (err) {
      console.error(err);
      showAlert(t('ats.positions.wizard.captacionBot.templateSaveError'), 'error');
    } finally {
      setSavingCaptationTpl(false);
    }
  };

  const handleSaveAsTemplate = async () => {
    const name = templateName.trim();
    if (!name) return;
    setSavingTemplate(true);
    try {
      const res = await api.post(
        `/projects/ats-pipeline-templates`,
        { name, pipeline: { phases: formData.pipeline?.phases || [] } },
        {
          params: { organizationId: organization, projectId: project },
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      if (res.data.response?.status_response === 'OK') {
        const created = res.data.response.data;
        setPipelineTemplates((prev) => [...prev, created]);
        setSelectedTemplateId(created.id);
        setPipelineTouched(false);
        setSaveTemplateOpen(false);
        setTemplateName('');
        showAlert(t('ats.positions.wizard.pipeline.templateSaved'), 'success');
      } else {
        showAlert(t('ats.positions.wizard.pipeline.templateSaveError'), 'error');
      }
    } catch (err) {
      console.error(err);
      showAlert(t('ats.positions.wizard.pipeline.templateSaveError'), 'error');
    } finally {
      setSavingTemplate(false);
    }
  };

  // Índices de ítems (bonos/beneficios) sin nombre: el nombre es obligatorio
  const missingNameIndexes = (items) => items
    .map((it, i) => (!(it.nombre || '').trim() ? i : -1))
    .filter((i) => i >= 0);

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t('ats.positions.wizard.required');
    if (!formData.industria.trim()) newErrors.industria = t('ats.positions.wizard.required');
    if (!formData.descripcion.trim()) newErrors.descripcion = t('ats.positions.wizard.required');
    const beneficiosErr = missingNameIndexes(formData.beneficios);
    if (beneficiosErr.length > 0) newErrors.beneficios = beneficiosErr;
    const bonosErr = missingNameIndexes(formData.bonos);
    if (bonosErr.length > 0) newErrors.bonos = bonosErr;
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      if (newErrors.name || newErrors.industria) setTabValue(0);
      else if (newErrors.descripcion || newErrors.beneficios) setTabValue(1);
      else if (newErrors.bonos) setTabValue(3);
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        ...formData,
        projectId: project,
        organizationId: organization,
        lastUpdatedBy: user?.email || user?.uid || 'unknown',
        numPosiciones: formData.numPosiciones ? parseInt(formData.numPosiciones) : null,
        // Rango y sueldo único son excluyentes: se anula el lado que no aplica.
        salarioRango: {
          tipo: formData.salarioRango.tipo === 'unico' ? 'unico' : 'rango',
          min: formData.salarioRango.tipo !== 'unico' && formData.salarioRango.min
            ? parseFloat(formData.salarioRango.min) : null,
          max: formData.salarioRango.tipo !== 'unico' && formData.salarioRango.max
            ? parseFloat(formData.salarioRango.max) : null,
          monto: formData.salarioRango.tipo === 'unico' && formData.salarioRango.monto
            ? parseFloat(formData.salarioRango.monto) : null,
          currency: formData.salarioRango.currency || null,
          visible: formData.salarioRango.visible,
        },
        bonos: formData.bonos.map((b) => ({
          nombre: b.nombre.trim(),
          descripcion: (b.descripcion || '').trim() || null,
          monto: b.monto !== '' && b.monto !== null && b.monto !== undefined
            ? parseFloat(b.monto) : null,
        })),
        beneficios: formData.beneficios.map((b) => ({
          nombre: b.nombre.trim(),
          descripcion: (b.descripcion || '').trim() || null,
        })),
        // Competencias predeterminadas (mismo campo que Gestión de Cargos)
        competencias: normalizeCompetencias(formData.competencias),
        // Solo se guarda con el opt-in marcado. Desmarcar el checkbox borra el
        // dato (null): es una acción deliberada del usuario.
        condicionesPago:
          condicionesPagoEnabled && formData.condicionesPago.precio
            ? {
              currency: formData.condicionesPago.currency || null,
              precio: parseFloat(formData.condicionesPago.precio),
              porcentajeKickoff: formData.condicionesPago.porcentajeKickoff !== ''
                ? parseFloat(formData.condicionesPago.porcentajeKickoff)
                : null,
              porcentajeCierre: formData.condicionesPago.porcentajeCierre !== ''
                ? parseFloat(formData.condicionesPago.porcentajeCierre)
                : null,
            }
            : null,
        fechaApertura: formData.fechaApertura || null,
        fechaCierre: formData.fechaCierre || null,
      };

      let response;
      if (isEdit) {
        response = await api.put(
          `/positions/${positionId}`,
          payload,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
      } else {
        response = await api.post(
          `/positions`,
          payload,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
      }

      if (response.data.response?.status_response === 'OK') {
        showAlert(
          isEdit
            ? t('ats.positions.positionUpdated')
            : t('ats.positions.positionCreated'),
          'success'
        );
        navigate(isEdit ? `/ats/positions/${positionId}` : '/ats/positions');
      } else {
        showAlert(t('ats.positions.saveError'), 'error');
      }
    } catch (err) {
      console.error(err);
      showAlert(t('ats.positions.saveError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{loadError}</Alert>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Box sx={{ width: '100%' }}>

        {/* Header */}
        <Box sx={{
          p: 3,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              {isEdit
                ? t('ats.positions.editPosition')
                : isDuplicate
                  ? t('ats.positions.duplicatePosition')
                  : t('ats.positions.newPosition')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('ats.positions.wizard.subtitle')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(isEdit ? `/ats/positions/${positionId}` : '/ats/positions')}
              disabled={saving}
            >
              {t('cancel')}
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              onClick={handleSubmit}
              disabled={saving}
              sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
            >
              {t('save')}
            </Button>
          </Box>
        </Box>

        {/* Tabs */}
        <Paper elevation={1}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={(_, v) => setTabValue(v)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab icon={<InfoIcon />} iconPosition="start" label={t('ats.positions.wizard.tabs.general')} />
              <Tab icon={<DescriptionIcon />} iconPosition="start" label={t('ats.positions.wizard.tabs.description')} />
              <Tab icon={<LocationIcon />} iconPosition="start" label={t('ats.positions.wizard.tabs.logistics')} />
              <Tab icon={<SalaryIcon />} iconPosition="start" label={t('ats.positions.wizard.tabs.salary')} />
              <Tab icon={<QuestionsIcon />} iconPosition="start" label={t('ats.positions.wizard.tabs.questions')} />
              <Tab icon={<PipelineIcon />} iconPosition="start" label={t('ats.positions.wizard.tabs.pipeline')} />
            </Tabs>
          </Box>

          <Box sx={{ p: 3 }}>

            {/* ── TAB 0 — GENERAL ── */}
            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={3}>

                <Grid item xs={12} md={8}>
                  <TextField
                    label={t('ats.positions.wizard.positionName')}
                    value={formData.name}
                    onChange={(e) => handleField('name', e.target.value)}
                    fullWidth
                    required
                    error={!!errors.name}
                    helperText={errors.name}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label={t('ats.positions.wizard.codigoRef')}
                    value={formData.codigoRef}
                    onChange={(e) => handleField('codigoRef', e.target.value)}
                    fullWidth
                    placeholder="REF-001"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label={t('ats.positions.wizard.internalName')}
                    value={formData.internalName}
                    onChange={(e) => handleField('internalName', e.target.value)}
                    fullWidth
                    helperText={t('ats.positions.wizard.internalNameHint')}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Autocomplete
                    fullWidth
                    freeSolo
                    options={Array.isArray(industriasOptions) ? industriasOptions : []}
                    inputValue={formData.industria || ''}
                    onInputChange={(_, newInputValue) => handleField('industria', newInputValue || '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('ats.positions.wizard.industry')}
                        required
                        error={!!errors.industria}
                        helperText={errors.industria}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('ats.positions.wizard.jerarquia')}
                    value={formData.jerarquia}
                    onChange={(e) => handleField('jerarquia', e.target.value)}
                    fullWidth
                    placeholder="Ej: Analista, Coordinador, Gerente"
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelWithInfo label={t('ats.positions.wizard.tipo')} tooltip={t('ats.positions.wizard.tooltips.tipo')} />
                  <ToggleButtonGroup
                    value={formData.tipo}
                    exclusive
                    size="small"
                    disabled={isEdit}
                    onChange={(_, val) => {
                      if (!val) return;
                      handleField('tipo', val);
                      // En creación/duplicado, el pipeline copiado puede no
                      // calzar con el tipo nuevo → volver a la plantilla estándar
                      if (!isEdit) {
                        handleField('pipeline', val === 'massive' ? massivePipeline() : buildDefaultPipeline());
                        setSelectedTemplateId('standard');
                        setPipelineTouched(false);
                      }
                    }}
                  >
                    {TIPOS_PROCESO.map((tp) => (
                      <ToggleButton key={tp} value={tp}>
                        {t(`ats.positions.wizard.options.tipo.${tp}`)}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  {isEdit && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {t('ats.positions.wizard.tipoLockedHint')}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('ats.positions.wizard.area')}
                    value={formData.area}
                    onChange={(e) => handleField('area', e.target.value)}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('ats.positions.wizard.subArea')}
                    value={formData.subArea}
                    onChange={(e) => handleField('subArea', e.target.value)}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label={t('ats.positions.wizard.numVacantes')}
                    value={formData.numPosiciones}
                    onChange={(e) => handleField('numPosiciones', e.target.value)}
                    fullWidth
                    type="number"
                    inputProps={{ min: 1 }}
                  />
                </Grid>

                <Grid item xs={12} md={8}>
                  <TextField
                    label={t('ats.positions.wizard.motivoBusqueda')}
                    value={formData.motivoBusqueda}
                    onChange={(e) => handleField('motivoBusqueda', e.target.value)}
                    fullWidth
                    placeholder={t('ats.positions.wizard.motivoBusquedaPlaceholder')}
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelWithInfo label={t('ats.positions.wizard.prioridad')} tooltip={t('ats.positions.wizard.tooltips.prioridad')} />
                  <ToggleButtonGroup
                    value={formData.prioridad}
                    exclusive
                    size="small"
                    onChange={(_, val) => { if (val) handleField('prioridad', val); }}
                  >
                    {PRIORIDADES.map((p) => (
                      <ToggleButton key={p} value={p}>
                        {t(`ats.positions.wizard.options.prioridad.${p}`)}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Grid>

                <Grid item xs={12}>
                  <LabelWithInfo label={t('ats.positions.wizard.statusLabel')} tooltip={t('ats.positions.wizard.tooltips.estado')} />
                  <ToggleButtonGroup
                    value={formData.status}
                    exclusive
                    size="small"
                    onChange={(_, val) => { if (val) handleField('status', val); }}
                  >
                    {STATUSES.map((s) => (
                      <ToggleButton key={s} value={s}>
                        {t(`ats.positions.wizard.options.status.${s}`)}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Grid>

                <Grid item xs={12}>
                  <LabelWithInfo label="Visibilidad" tooltip={t('ats.positions.wizard.tooltips.visibilidad')} />
                  <ToggleButtonGroup
                    value={formData.visibility}
                    exclusive
                    size="small"
                    onChange={(_, val) => { if (val) handleField('visibility', val); }}
                  >
                    {VISIBILITIES.map((visibility) => (
                      <ToggleButton key={visibility} value={visibility}>
                        {visibility === 'confidential' ? 'Confidencial' : 'General'}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Las vacantes confidenciales solo serán visibles para administradores y para el usuario que las creó.
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formData.autoSendRejectionEmail}
                        onChange={(e) => handleField('autoSendRejectionEmail', e.target.checked)}
                      />
                    }
                    label={t('ats.positions.wizard.autoSendRejectionEmail')}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {t('ats.positions.wizard.autoSendRejectionEmailHelper')}
                  </Typography>
                </Grid>

              </Grid>
            </TabPanel>

            {/* ── TAB 1 — DESCRIPCIÓN ── */}
            <TabPanel value={tabValue} index={1}>
              <Grid container spacing={3}>

                <Grid item xs={12}>
                  <TextField
                    label={t('ats.positions.wizard.descripcion')}
                    value={formData.descripcion}
                    onChange={(e) => handleField('descripcion', e.target.value)}
                    fullWidth
                    required
                    multiline
                    rows={5}
                    error={!!errors.descripcion}
                    helperText={errors.descripcion}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label={t('ats.positions.wizard.responsabilidades')}
                    value={formData.responsabilidades}
                    onChange={(e) => handleField('responsabilidades', e.target.value)}
                    fullWidth
                    multiline
                    rows={5}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label={t('ats.positions.wizard.perfil')}
                    value={formData.perfil}
                    onChange={(e) => handleField('perfil', e.target.value)}
                    fullWidth
                    multiline
                    rows={3}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('ats.positions.wizard.conocimientosExcluyentes')}
                    value={formData.conocimientosExcluyentes}
                    onChange={(e) => handleField('conocimientosExcluyentes', e.target.value)}
                    fullWidth
                    multiline
                    rows={3}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label={t('ats.positions.wizard.conocimientosDeseables')}
                    value={formData.conocimientosDeseables}
                    onChange={(e) => handleField('conocimientosDeseables', e.target.value)}
                    fullWidth
                    multiline
                    rows={3}
                  />
                </Grid>

                {/* Competencias predeterminadas del cargo (mismo campo que Gestión
                    de Cargos): se precargan al enviar un DISC en modo completo y
                    las usa la IA al sugerir preguntas de entrevista. */}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={competenciesOptions}
                    value={formData.competencias || []}
                    onChange={(_, newValue) => handleField('competencias', newValue.map(normalizeCompetencia))}
                    getOptionLabel={(option) => option.competencyName ?? option.name ?? ''}
                    isOptionEqualToValue={(option, value) =>
                      (option.competencyId ?? option.id ?? option.uid) ===
                      (value.competencyId ?? value.id ?? value.uid)
                    }
                    loading={loadingCompetencies}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id ?? option.uid}>
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={`${t('ats.positions.wizard.competencias')} ${t('disc.optional')}`}
                        helperText={t('ats.positions.wizard.competenciasHelper')}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingCompetencies ? <CircularProgress size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="URL de información adicional"
                    value={formData.additionalInfoUrl}
                    onChange={(e) => handleField('additionalInfoUrl', e.target.value)}
                    fullWidth
                    placeholder="https://..."
                    helperText="Puedes agregar un enlace complementario para la postulación, como video, documento, carpeta, sitio externo u otro recurso."
                  />
                </Grid>

                {/* Beneficios del cargo: lista propia de la vacante (parte vacía).
                    Se muestran al candidato y el bot los usa si le preguntan. */}
                <Grid item xs={12}>
                  <Divider sx={{ mb: 2 }} />
                  <LabelWithInfo
                    label={t('ats.positions.wizard.beneficios.title')}
                    tooltip={t('ats.positions.wizard.beneficios.tooltip')}
                  />
                  <ItemListEditor
                    items={formData.beneficios}
                    onChange={(items) => handleField('beneficios', items)}
                    errorIndexes={errors.beneficios || []}
                    emptyText={t('ats.positions.wizard.beneficios.empty')}
                    labels={{
                      nombre: t('ats.positions.wizard.beneficios.nombre'),
                      descripcion: t('ats.positions.wizard.beneficios.descripcion'),
                      add: t('ats.positions.wizard.beneficios.add'),
                      remove: t('ats.positions.wizard.beneficios.remove'),
                      required: t('ats.positions.wizard.required'),
                    }}
                  />
                </Grid>

              </Grid>
            </TabPanel>

            {/* ── TAB 2 — LOGÍSTICA ── */}
            <TabPanel value={tabValue} index={2}>
              <Grid container spacing={3}>

                <Grid item xs={12}>
                  <LabelWithInfo label={t('ats.positions.wizard.modalidad')} tooltip={t('ats.positions.wizard.tooltips.modalidad')} />
                  <ToggleButtonGroup
                    value={formData.modalidad}
                    exclusive
                    size="small"
                    onChange={(_, val) => { if (val) handleField('modalidad', val); }}
                  >
                    {MODALIDADES.map((m) => (
                      <ToggleButton key={m} value={m}>
                        {t(`ats.positions.wizard.options.modalidad.${m}`)}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Grid>

                <Grid item xs={12}><Divider /></Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label={t('ats.positions.wizard.country')}
                    value={formData.ubicacion.country}
                    onChange={(e) => handleNestedField('ubicacion', 'country', e.target.value)}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label={t('ats.positions.wizard.province')}
                    value={formData.ubicacion.province}
                    onChange={(e) => handleNestedField('ubicacion', 'province', e.target.value)}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label={t('ats.positions.wizard.city')}
                    value={formData.ubicacion.city}
                    onChange={(e) => handleNestedField('ubicacion', 'city', e.target.value)}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12}><Divider /></Grid>

                <Grid item xs={12} md={6}>
                  <DatePicker
                    label={t('ats.positions.wizard.fechaApertura')}
                    value={formData.fechaApertura ? dayjs(formData.fechaApertura) : null}
                    onChange={(val) => handleField('fechaApertura', val ? val.format('YYYY-MM-DD') : '')}
                    format="DD/MM/YYYY"
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <DatePicker
                    label={t('ats.positions.wizard.fechaCierre')}
                    value={formData.fechaCierre ? dayjs(formData.fechaCierre) : null}
                    onChange={(val) => handleField('fechaCierre', val ? val.format('YYYY-MM-DD') : '')}
                    format="DD/MM/YYYY"
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>

                <Grid item xs={12}><Divider /></Grid>

                <Grid item xs={12} md={6}>
                  <LabelWithInfo label={t('ats.positions.wizard.tipoBusqueda')} tooltip={t('ats.positions.wizard.tooltips.tipoBusqueda')} />
                  <ToggleButtonGroup
                    value={formData.tipoBusqueda}
                    exclusive
                    size="small"
                    onChange={(_, val) => { if (val) handleField('tipoBusqueda', val); }}
                  >
                    {TIPOS_BUSQUEDA.map((v) => (
                      <ToggleButton key={v} value={v}>
                        {t(`ats.positions.wizard.options.tipoBusqueda.${v}`)}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Grid>

              </Grid>
            </TabPanel>

            {/* ── TAB 3 — RENTA ── */}
            <TabPanel value={tabValue} index={3}>
              <Grid container spacing={3}>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.salarioRango.visible}
                        onChange={(e) => handleSalarioRango('visible', e.target.checked)}
                      />
                    }
                    label={t('ats.positions.wizard.salarioVisible')}
                  />
                </Grid>

                {/* Rango o sueldo único: dos formas alternativas de indicar la renta */}
                <Grid item xs={12}>
                  <LabelWithInfo
                    label={t('ats.positions.wizard.salarioTipo')}
                    tooltip={t('ats.positions.wizard.tooltips.salarioTipo')}
                  />
                  <ToggleButtonGroup
                    value={formData.salarioRango.tipo}
                    exclusive
                    size="small"
                    onChange={(_, val) => { if (val) handleSalarioRango('tipo', val); }}
                  >
                    {TIPOS_SALARIO.map((ts) => (
                      <ToggleButton key={ts} value={ts}>
                        {t(`ats.positions.wizard.options.salarioTipo.${ts}`)}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Autocomplete
                    options={CURRENCIES}
                    value={formData.salarioRango.currency}
                    onChange={(_, val) => handleSalarioRango('currency', val)}
                    freeSolo
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('ats.positions.wizard.currency')}
                        placeholder="CLP"
                        onChange={(e) => handleSalarioRango('currency', e.target.value)}
                      />
                    )}
                  />
                </Grid>

                {formData.salarioRango.tipo === 'unico' && (
                  <Grid item xs={12} md={4}>
                    <TextField
                      label={t('ats.positions.wizard.salarioMonto')}
                      value={formData.salarioRango.monto}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || parseFloat(val) >= 0) handleSalarioRango('monto', val);
                      }}
                      fullWidth
                      type="number"
                      inputProps={{ min: 0 }}
                      InputProps={{
                        startAdornment: formData.salarioRango.currency && (
                          <InputAdornment position="start">
                            {formData.salarioRango.currency}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                )}

                {formData.salarioRango.tipo !== 'unico' && (
                <>
                <Grid item xs={12} md={4}>
                  <TextField
                    label={t('ats.positions.wizard.salarioMin')}
                    value={formData.salarioRango.min}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || parseFloat(val) >= 0) handleSalarioRango('min', val);
                    }}
                    fullWidth
                    type="number"
                    inputProps={{ min: 0 }}
                    InputProps={{
                      startAdornment: formData.salarioRango.currency && (
                        <InputAdornment position="start">
                          {formData.salarioRango.currency}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label={t('ats.positions.wizard.salarioMax')}
                    value={formData.salarioRango.max}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || parseFloat(val) >= 0) handleSalarioRango('max', val);
                    }}
                    fullWidth
                    type="number"
                    inputProps={{ min: 0 }}
                    InputProps={{
                      startAdornment: formData.salarioRango.currency && (
                        <InputAdornment position="start">
                          {formData.salarioRango.currency}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                </>
                )}

                {/* Bonos del cargo: contexto para el bot (respetan el switch
                    "mostrar salario"). No participan en cálculos ni reportes. */}
                <Grid item xs={12}>
                  <Divider sx={{ mb: 2 }} />
                  <LabelWithInfo
                    label={t('ats.positions.wizard.bonos.title')}
                    tooltip={t('ats.positions.wizard.bonos.tooltip')}
                  />
                  <ItemListEditor
                    items={formData.bonos}
                    onChange={(items) => handleField('bonos', items)}
                    showMonto
                    currency={formData.salarioRango.currency}
                    errorIndexes={errors.bonos || []}
                    emptyText={t('ats.positions.wizard.bonos.empty')}
                    labels={{
                      nombre: t('ats.positions.wizard.bonos.nombre'),
                      descripcion: t('ats.positions.wizard.bonos.descripcion'),
                      monto: t('ats.positions.wizard.bonos.monto'),
                      add: t('ats.positions.wizard.bonos.add'),
                      remove: t('ats.positions.wizard.bonos.remove'),
                      required: t('ats.positions.wizard.required'),
                    }}
                  />
                </Grid>

                {/* Condiciones económicas del encargo (consultora ↔ cliente).
                    Opt-in explícito. DATO INTERNO: no se muestra al candidato
                    en el portal ni en el bot de captación. */}
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 1 }} />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={condicionesPagoEnabled}
                        onChange={(e) => setCondicionesPagoEnabled(e.target.checked)}
                      />
                    }
                    label={t('ats.positions.wizard.condicionesPago.enable')}
                  />
                </Grid>

                {condicionesPagoEnabled && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2.5, bgcolor: 'grey.50' }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                        {t('ats.positions.wizard.condicionesPago.title')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        {t('ats.positions.wizard.condicionesPago.internalNote')}
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                          <Autocomplete
                            options={CURRENCIES}
                            value={formData.condicionesPago.currency}
                            onChange={(_, val) => handleNestedField('condicionesPago', 'currency', val)}
                            freeSolo
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={t('ats.positions.wizard.currency')}
                                placeholder="CLP"
                                onChange={(e) => handleNestedField('condicionesPago', 'currency', e.target.value)}
                              />
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} md={3}>
                          <TextField
                            label={t('ats.positions.wizard.condicionesPago.precio')}
                            value={formData.condicionesPago.precio}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '' || parseFloat(val) >= 0) {
                                handleNestedField('condicionesPago', 'precio', val);
                              }
                            }}
                            fullWidth
                            type="number"
                            inputProps={{ min: 0 }}
                            InputProps={{
                              startAdornment: formData.condicionesPago.currency && (
                                <InputAdornment position="start">
                                  {formData.condicionesPago.currency}
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={3}>
                          <TextField
                            label={t('ats.positions.wizard.condicionesPago.kickoff')}
                            value={formData.condicionesPago.porcentajeKickoff}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '' || (parseFloat(val) >= 0 && parseFloat(val) <= 100)) {
                                handlePorcentajeTramo('porcentajeKickoff', val);
                              }
                            }}
                            fullWidth
                            type="number"
                            inputProps={{ min: 0, max: 100 }}
                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                            helperText={montoTramo(formData.condicionesPago.porcentajeKickoff)}
                          />
                        </Grid>

                        <Grid item xs={12} md={3}>
                          <TextField
                            label={t('ats.positions.wizard.condicionesPago.cierre')}
                            value={formData.condicionesPago.porcentajeCierre}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '' || (parseFloat(val) >= 0 && parseFloat(val) <= 100)) {
                                handlePorcentajeTramo('porcentajeCierre', val);
                              }
                            }}
                            fullWidth
                            type="number"
                            inputProps={{ min: 0, max: 100 }}
                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                            helperText={montoTramo(formData.condicionesPago.porcentajeCierre)}
                          />
                        </Grid>

                      </Grid>
                    </Paper>
                  </Grid>
                )}

              </Grid>
            </TabPanel>

            {/* ── TAB 4 — PREGUNTAS ── */}
            <TabPanel value={tabValue} index={4}>
              <AtsFilteringQuestions
                positionId={isEdit ? positionId : null}
                questions={formData.filteringQuestions}
                onChange={(questions) => handleField('filteringQuestions', questions)}
              />
            </TabPanel>

            {/* ── TAB 5 — PIPELINE ── */}
            <TabPanel value={tabValue} index={5}>
              {(
                <>
                  {/* ── Parte 1 (solo masivas): captación por bot ── */}
                  {formData.tipo === 'massive' && (
                    <Box sx={{ mb: 4 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                            {t('ats.positions.wizard.captacionBot.title')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {(formData.captacion?.enabled ?? true)
                              ? t('ats.positions.wizard.captacionBot.enabledHint')
                              : t('ats.positions.wizard.captacionBot.disabledHint')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                            {formData.captacion?.portalEnabled === true
                              ? t('ats.positions.wizard.captacionBot.portalEnabledHint')
                              : t('ats.positions.wizard.captacionBot.portalDisabledHint')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                          <FormControlLabel
                            control={(
                              <Switch
                                checked={formData.captacion?.enabled ?? true}
                                onChange={(e) => handleField('captacion', { ...(formData.captacion || {}), enabled: e.target.checked })}
                              />
                            )}
                            label={t('ats.positions.wizard.captacionBot.switchLabel')}
                          />
                          <FormControlLabel
                            control={(
                              <Switch
                                checked={formData.captacion?.portalEnabled === true}
                                onChange={(e) => handleField('captacion', { ...(formData.captacion || { enabled: true }), portalEnabled: e.target.checked })}
                              />
                            )}
                            label={t('ats.positions.wizard.captacionBot.portalSwitchLabel')}
                          />
                          <Tooltip title={t('ats.positions.wizard.captacionBot.limitHint')} arrow>
                            <TextField
                              size="small"
                              type="number"
                              label={t('ats.positions.wizard.captacionBot.limitLabel')}
                              placeholder={t('ats.positions.wizard.captacionBot.limitPlaceholder')}
                              value={formData.captacion?.limit ?? ''}
                              onChange={(e) => {
                                const raw = e.target.value;
                                const limit = raw === '' ? null : Math.max(1, parseInt(raw, 10) || 1);
                                handleField('captacion', { ...(formData.captacion || { enabled: true }), limit });
                              }}
                              // El límite vale para los dos canales (bot + formulario)
                              disabled={!((formData.captacion?.enabled ?? true) || formData.captacion?.portalEnabled === true)}
                              sx={{ width: 170 }}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Tooltip>
                        </Box>
                      </Box>
                      {/* Selector de plantilla de captación — solo al crear (F6).
                          La default del proyecto viene aplicada; cambiarla o
                          editar abajo NO toca la plantilla (snapshot). */}
                      {!isEdit && (
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                          <TextField
                            select
                            size="small"
                            label={t('ats.positions.wizard.captacionBot.templateSelector')}
                            value={selectedCaptationTemplateId}
                            onChange={(e) => handleSelectCaptationTemplate(e.target.value)}
                            sx={{ minWidth: 300 }}
                          >
                            <MenuItem value="standard">
                              {t('ats.positions.wizard.captacionBot.templateStandard')}
                            </MenuItem>
                            {captationTemplates.map((tpl) => (
                              <MenuItem key={tpl.id} value={tpl.id}>
                                {tpl.name}
                                {tpl.isDefault
                                  ? ` (${t('ats.positions.wizard.pipeline.templateDefaultSuffix')})`
                                  : ''}
                              </MenuItem>
                            ))}
                          </TextField>
                          {isAdminPlus && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<SaveTemplateIcon />}
                              onClick={() => setSaveCaptationTplOpen(true)}
                              sx={{
                                borderColor: '#6A5AF9',
                                color: '#6A5AF9',
                                '&:hover': { borderColor: '#5B4DE8', backgroundColor: '#F5F3FF' },
                              }}
                            >
                              {t('ats.positions.wizard.captacionBot.saveAsTemplate')}
                            </Button>
                          )}
                        </Box>
                      )}
                      {/* Las mismas preguntas valen para el bot y el formulario: se
                          atenúa solo con los dos canales apagados */}
                      <Box sx={{ opacity: ((formData.captacion?.enabled ?? true) || formData.captacion?.portalEnabled === true) ? 1 : 0.5, pointerEvents: ((formData.captacion?.enabled ?? true) || formData.captacion?.portalEnabled === true) ? 'auto' : 'none' }}>
                        <AtsCaptationFlowEditor
                          value={formData.captacion?.flow || null}
                          onChange={(flow) => handleField('captacion', { ...(formData.captacion || { enabled: true }), flow })}
                          rules={formData.captacion?.rules || null}
                          onRulesChange={(rules) => handleField('captacion', { ...(formData.captacion || { enabled: true }), rules })}
                          filteringQuestions={formData.filteringQuestions || []}
                        />
                      </Box>
                    </Box>
                  )}

                  {/* Hunting: solo "Visible en portal" (no tiene bot de captación).
                      Apagado: no sale en la web de empleos y su link no recibe
                      postulaciones (PLAN_MASIVO_PORTAL) */}
                  {formData.tipo !== 'massive' && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                          {t('ats.positions.wizard.portalVisibility.title')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {isVisibleInPortal(formData)
                            ? t('ats.positions.wizard.portalVisibility.onHint')
                            : t('ats.positions.wizard.portalVisibility.offHint')}
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={(
                          <Switch
                            checked={isVisibleInPortal(formData)}
                            onChange={(e) => handleField('captacion', { ...(formData.captacion || { enabled: true }), portalEnabled: e.target.checked })}
                          />
                        )}
                        label={t('ats.positions.wizard.captacionBot.portalSwitchLabel')}
                      />
                    </Box>
                  )}

                  {formData.tipo === 'massive' && (
                    <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                      {t('ats.positions.wizard.captacionBot.pipelinePartTitle')}
                    </Typography>
                  )}

                  {/* Selector de plantilla — solo al crear la vacante */}
                  {!isEdit && (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
                      <TextField
                        select
                        size="small"
                        label={t('ats.positions.wizard.pipeline.templateSelector')}
                        value={selectedTemplateId}
                        onChange={(e) => handleSelectTemplate(e.target.value)}
                        sx={{ minWidth: 300 }}
                      >
                        <MenuItem value="standard">
                          {t('ats.positions.wizard.pipeline.templateStandard')}
                        </MenuItem>
                        {pipelineTemplates.map((tpl) => (
                          <MenuItem key={tpl.id} value={tpl.id}>
                            {tpl.name}
                            {tpl.isDefault
                              ? ` (${t('ats.positions.wizard.pipeline.templateDefaultSuffix')})`
                              : ''}
                          </MenuItem>
                        ))}
                      </TextField>
                      {isAdminPlus && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<SaveTemplateIcon />}
                          onClick={() => setSaveTemplateOpen(true)}
                          sx={{
                            borderColor: '#6A5AF9',
                            color: '#6A5AF9',
                            '&:hover': { borderColor: '#5B4DE8', backgroundColor: '#F5F3FF' },
                          }}
                        >
                          {t('ats.positions.wizard.pipeline.saveAsTemplate')}
                        </Button>
                      )}
                    </Box>
                  )}

                  <AtsPipelineConfigurator
                    pipeline={formData.pipeline}
                    onChange={(pipeline) => {
                      if (!isEdit) setPipelineTouched(true);
                      handleField('pipeline', pipeline);
                    }}
                  />
                </>
              )}
            </TabPanel>

          </Box>
        </Paper>

        {/* Dialog: confirmar el reemplazo del pipeline configurado por una plantilla */}
        <Dialog
          open={Boolean(pendingTemplateId)}
          onClose={() => setPendingTemplateId(null)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            {t('ats.positions.wizard.pipeline.templateReplaceTitle')}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              {t('ats.positions.wizard.pipeline.templateReplaceConfirm')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPendingTemplateId(null)}>
              {t('cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                applyPipelineTemplate(pendingTemplateId);
                setPendingTemplateId(null);
              }}
              sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
            >
              {t('ats.positions.wizard.pipeline.templateReplaceAction')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog: guardar el pipeline actual como plantilla del proyecto */}
        <Dialog
          open={saveTemplateOpen}
          onClose={() => { if (!savingTemplate) setSaveTemplateOpen(false); }}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            {t('ats.positions.wizard.pipeline.saveAsTemplateTitle')}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('ats.positions.wizard.pipeline.saveAsTemplateHint')}
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label={t('ats.positions.wizard.pipeline.templateNameField')}
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveAsTemplate(); }}
              inputProps={{ maxLength: 80 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSaveTemplateOpen(false)} disabled={savingTemplate}>
              {t('cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveAsTemplate}
              disabled={savingTemplate || !templateName.trim()}
              startIcon={savingTemplate ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
            >
              {t('save')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog: guardar la captación actual como plantilla del proyecto (F6) */}
        <Dialog
          open={saveCaptationTplOpen}
          onClose={() => { if (!savingCaptationTpl) setSaveCaptationTplOpen(false); }}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            {t('ats.positions.wizard.captacionBot.saveAsTemplateTitle')}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('ats.positions.wizard.captacionBot.saveAsTemplateHint')}
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label={t('ats.positions.wizard.pipeline.templateNameField')}
              value={captationTplName}
              onChange={(e) => setCaptationTplName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveCaptationAsTemplate(); }}
              inputProps={{ maxLength: 80 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSaveCaptationTplOpen(false)} disabled={savingCaptationTpl}>
              {t('cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveCaptationAsTemplate}
              disabled={savingCaptationTpl || !captationTplName.trim()}
              startIcon={savingCaptationTpl ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } }}
            >
              {t('save')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default AtsPositionWizard;